import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { userApi } from "../../api/user.api";
import { queryKeys } from "../../lib/react-query/queryKeys";
import type { CustomTemplate, UserProfile } from "../../types/userTypes";

/**
 * Hook to fetch current user profile
 */
export const useUserProfile = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.user.profile(),
    queryFn: userApi.getProfile,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

/**
 * Hook to update current user profile
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.updateProfile,
    onMutate: async (updatedData: Partial<UserProfile>) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.user.profile() });

      const previousProfile =
        queryClient.getQueryData<UserProfile | null>(queryKeys.user.profile());

      if (previousProfile) {
        queryClient.setQueryData<UserProfile | null>(
          queryKeys.user.profile(),
          {
            ...previousProfile,
            ...updatedData,
            updatedAt: new Date().toISOString(),
          },
        );
      }

      return { previousProfile };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(
          queryKeys.user.profile(),
          context.previousProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
    },
  });
};

/**
 * Hook to change the current user's specialty.
 *
 * Kept separate from useUpdateUserProfile: specialty goes through the
 * updateUserSpecialty callable (server-side country/specialty validation),
 * not the direct users/{uid} updateDoc — see userApi.updateSpecialty.
 */
export const useUpdateUserSpecialty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.updateSpecialty,
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(queryKeys.user.profile(), updatedProfile);
    },
  });
};

/**
 * Hook to refresh user profile
 */
export const useRefreshUserProfile = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
};

/**
 * Hook to fetch user's custom templates from userTemplates/{uid}.customTemplates
 */
export const useUserCustomTemplates = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.user.customTemplates(),
    queryFn: userApi.getUserCustomTemplates,
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

/**
 * Hook to save (create or update) a single custom template
 */
export const useSaveUserCustomTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (template: CustomTemplate) => userApi.saveUserCustomTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.customTemplates() });
    },
  });
};

/**
 * Hook to delete a single custom template by id
 */
export const useDeleteUserCustomTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => userApi.deleteUserCustomTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.customTemplates() });
    },
  });
};
